import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, Building2, User, Mail, Phone, Landmark, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  Albero dell'anagrafica (richiesta titolare 08/09/2026).
  Unità → gruppi di titolarità → soggetti:
    - Proprietà (proprietario, comproprietari)
    - Nuda proprietà / usufrutto
    - Conduzione (inquilini)
    - Storico (ex proprietari, titolarità chiuse)
  Ogni unità porta i dati catastali, modificabili qui; ogni gruppo permette di aggiungere
  un soggetto. La fonte è SEMPRE ciò che sta nell'app: nessun dato viene rigenerato da
  file esterni.
*/

const RUOLI = {
  OWNER: { label: 'Proprietario', color: 'bg-blue-100 text-blue-800' },
  CO_OWNER: { label: 'Comproprietario', color: 'bg-indigo-100 text-indigo-800' },
  BARE_OWNER: { label: 'Nudo proprietario', color: 'bg-purple-100 text-purple-800' },
  USUFRUCTUARY: { label: 'Usufruttuario', color: 'bg-amber-100 text-amber-800' },
  TENANT: { label: 'Inquilino', color: 'bg-green-100 text-green-800' },
  EX_OWNER: { label: 'Ex proprietario', color: 'bg-stone-200 text-stone-700' },
};

const GRUPPI = [
  { key: 'PROPRIETA', label: 'Proprietà', roles: ['OWNER', 'CO_OWNER'], ruoloNuovo: 'OWNER' },
  { key: 'USUFRUTTO', label: 'Nuda proprietà / usufrutto', roles: ['BARE_OWNER', 'USUFRUCTUARY'], ruoloNuovo: 'USUFRUCTUARY' },
  { key: 'CONDUZIONE', label: 'Conduzione (inquilini)', roles: ['TENANT'], ruoloNuovo: 'TENANT' },
  { key: 'FUTURO', label: 'Titolarità future (non ancora attive)', roles: [] },
  { key: 'STORICO', label: 'Storico (ex proprietari, titolarità chiuse)', roles: ['EX_OWNER'], ruoloNuovo: 'EX_OWNER' },
];

const DESTINAZIONI = {
  ABITAZIONE: 'Abitazione', UFFICIO: 'Ufficio', NEGOZIO: 'Negozio', BOX: 'Box', MAGAZZINO: 'Magazzino',
  ALLOGGIO_PORTIERE: 'Alloggio portiere', ALTRO: 'Altro',
};

const CATASTO_VUOTO = { cadastral_zone: '', cadastral_sheet: '', cadastral_parcel: '', cadastral_subordinate: '', cadastral_category: '', cadastral_income: '' };

const chiaveUnita = (n) => {
  const m = /^([A-Za-z]*)(\d+)/.exec(String(n || ''));
  return m ? [m[1], parseInt(m[2], 10)] : [String(n || ''), 0];
};
const ordinaUnita = (a, b) => {
  const [la, na] = chiaveUnita(a.unit_number);
  const [lb, nb] = chiaveUnita(b.unit_number);
  return la.localeCompare(lb) || na - nb;
};

const oggi = () => new Date().toISOString().slice(0, 10);
const attiva = (rel) => {
  if (rel.relationship_type === 'EX_OWNER') return false;
  if (rel.valid_from && String(rel.valid_from).slice(0, 10) > oggi()) return false;
  if (!rel.valid_to) return true;
  return String(rel.valid_to).slice(0, 10) >= oggi();
};
const gruppoDi = (rel) => {
  if (rel.relationship_type !== 'EX_OWNER' && rel.valid_from && String(rel.valid_from).slice(0, 10) > oggi()) return 'FUTURO';
  if (!attiva(rel)) return 'STORICO';
  const g = GRUPPI.find((x) => x.roles.includes(rel.relationship_type));
  return g ? g.key : 'STORICO';
};

const catastoRiassunto = (u) => {
  const parti = [];
  if (u.cadastral_sheet) parti.push(`Fg. ${u.cadastral_sheet}`);
  if (u.cadastral_parcel) parti.push(`Part. ${u.cadastral_parcel}`);
  if (u.cadastral_subordinate) parti.push(`Sub. ${u.cadastral_subordinate}`);
  if (u.cadastral_category) parti.push(`Cat. ${u.cadastral_category}`);
  if (u.cadastral_income) parti.push(`Rendita € ${Number(u.cadastral_income).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`);
  return parti.join(' · ');
};

export const TreeTab = ({ condominiumId, isAdmin }) => {
  const [units, setUnits] = useState([]);
  const [people, setPeople] = useState([]);
  const [rels, setRels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aperte, setAperte] = useState({});
  const [cerca, setCerca] = useState('');
  const [vista, setVista] = useState('unita');           // 'unita' | 'soggetti'
  const [mostraStorico, setMostraStorico] = useState(false);

  // dialogo catasto
  const [catastoUnit, setCatastoUnit] = useState(null);
  const [catasto, setCatasto] = useState(CATASTO_VUOTO);
  // dialogo nuovo soggetto sull'unità
  const [nuovo, setNuovo] = useState(null);                // {unit, gruppo}
  const [nuovoForm, setNuovoForm] = useState({ person_id: '', relationship_type: 'OWNER', ownership_percentage: '', to_verify: false, notes: '' });
  const [cercaPersona, setCercaPersona] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p, r] = await Promise.all([
        axios.get(`${API}/condominiums/${condominiumId}/units`),
        axios.get(`${API}/condominiums/${condominiumId}/people`),
        axios.get(`${API}/condominiums/${condominiumId}/relationships`),
      ]);
      setUnits((u.data || []).slice().sort(ordinaUnita));
      setPeople((p.data || []).slice().sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
      setRels(r.data || []);
    } catch (e) {
      toast.error("Errore nel caricamento dell'anagrafica");
    } finally {
      setLoading(false);
    }
  }, [condominiumId]);

  useEffect(() => { carica(); }, [carica]);

  const personaDi = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);

  // unità → gruppo → titolarità
  const albero = useMemo(() => {
    const perUnita = {};
    for (const r of rels) {
      const g = gruppoDi(r);
      perUnita[r.unit_id] = perUnita[r.unit_id] || {};
      (perUnita[r.unit_id][g] = perUnita[r.unit_id][g] || []).push(r);
    }
    return perUnita;
  }, [rels]);

  const q = cerca.trim().toLowerCase();
  const unitaVisibili = useMemo(() => {
    if (!q) return units;
    return units.filter((u) => {
      if ((u.unit_number || '').toLowerCase().includes(q)) return true;
      const gruppi = albero[u.id] || {};
      return Object.values(gruppi).flat().some((r) => (personaDi[r.person_id]?.full_name || r.person_name || '').toLowerCase().includes(q));
    });
  }, [units, q, albero, personaDi]);

  const tuttiAperti = () => setAperte(Object.fromEntries(units.map((u) => [u.id, true])));
  const tuttiChiusi = () => setAperte({});
  const toggle = (id) => setAperte((s) => ({ ...s, [id]: !s[id] }));

  // ---- catasto ----
  const apriCatasto = (u) => {
    setCatastoUnit(u);
    setCatasto({
      cadastral_zone: u.cadastral_zone || '', cadastral_sheet: u.cadastral_sheet || '', cadastral_parcel: u.cadastral_parcel || '',
      cadastral_subordinate: u.cadastral_subordinate || '', cadastral_category: u.cadastral_category || '',
      cadastral_income: u.cadastral_income ?? '',
    });
  };
  const salvaCatasto = async () => {
    if (!catastoUnit) return;
    setSalvando(true);
    try {
      const body = { unit_number: catastoUnit.unit_number };
      for (const [k, v] of Object.entries(catasto)) {
        if (k === 'cadastral_income') body[k] = v === '' ? null : parseFloat(String(v).replace(',', '.'));
        else body[k] = v === '' ? null : v;
      }
      await axios.put(`${API}/condominiums/${condominiumId}/units/${catastoUnit.id}`, body);
      toast.success(`Dati catastali di ${catastoUnit.unit_number} salvati`);
      setCatastoUnit(null);
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio dei dati catastali');
    } finally {
      setSalvando(false);
    }
  };

  // ---- nuovo soggetto sull'unità ----
  const apriNuovo = (unit, gruppo) => {
    setNuovo({ unit, gruppo });
    setNuovoForm({ person_id: '', relationship_type: gruppo.ruoloNuovo, ownership_percentage: '', to_verify: false, notes: '' });
    setCercaPersona('');
  };
  const salvaNuovo = async () => {
    if (!nuovo || !nuovoForm.person_id) { toast.error('Scegli il soggetto'); return; }
    setSalvando(true);
    try {
      await axios.post(`${API}/condominiums/${condominiumId}/units/${nuovo.unit.id}/relationships`, {
        person_id: nuovoForm.person_id,
        relationship_type: nuovoForm.relationship_type,
        ownership_percentage: nuovoForm.ownership_percentage ? parseFloat(String(nuovoForm.ownership_percentage).replace(',', '.')) : null,
        to_verify: !!nuovoForm.to_verify,
        notes: nuovoForm.notes || null,
      });
      toast.success('Soggetto collegato all\'unità');
      setNuovo(null);
      setAperte((s) => ({ ...s, [nuovo.unit.id]: true }));
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel collegamento');
    } finally {
      setSalvando(false);
    }
  };
  const rimuovi = async (rel) => {
    const nome = personaDi[rel.person_id]?.full_name || rel.person_name || '';
    if (!window.confirm(`Rimuovere ${nome} da ${rel.unit_number || ''}? Se ha già addebiti, la titolarità viene chiusa a oggi invece che cancellata.`)) return;
    try {
      await axios.delete(`${API}/condominiums/${condominiumId}/units/${rel.unit_id}/relationships/${rel.id}`);
      toast.success('Titolarità rimossa');
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const personeFiltrate = useMemo(() => {
    const qq = cercaPersona.trim().toLowerCase();
    const lista = qq ? people.filter((p) => (p.full_name || '').toLowerCase().includes(qq)) : people;
    return lista.slice(0, 60);
  }, [people, cercaPersona]);

  // ---- vista per soggetto ----
  const perSoggetto = useMemo(() => {
    const m = {};
    for (const r of rels) (m[r.person_id] = m[r.person_id] || []).push(r);
    return m;
  }, [rels]);

  const RigaPersona = ({ rel }) => {
    const p = personaDi[rel.person_id] || {};
    const ruolo = RUOLI[rel.relationship_type] || { label: rel.relationship_type, color: 'bg-gray-100 text-gray-800' };
    return (
      <div className="flex flex-wrap items-center gap-2 py-1.5 pl-8 text-sm border-b last:border-b-0" data-testid={`albero-titolarita-${rel.id}`}>
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{p.full_name || rel.person_name || '—'}</span>
        <Badge className={ruolo.color}>{ruolo.label}{rel.ownership_percentage ? ` ${rel.ownership_percentage}%` : ''}</Badge>
        {rel.to_verify && <Badge variant="outline" className="text-amber-700 border-amber-300"><AlertTriangle className="h-3 w-3 mr-1" />da verificare</Badge>}
        {rel.valid_from && <span className="text-xs text-muted-foreground">dal {String(rel.valid_from).slice(0, 10)}</span>}
        {rel.valid_to && <span className="text-xs text-muted-foreground">fino al {String(rel.valid_to).slice(0, 10)}</span>}
        <span className="text-xs text-muted-foreground flex items-center gap-2 ml-2">
          {p.pec && <span title="PEC"><Mail className="inline h-3 w-3 mr-0.5" />{p.pec}</span>}
          {!p.pec && p.email && <span title="Email"><Mail className="inline h-3 w-3 mr-0.5" />{p.email}</span>}
          {(p.mobile || p.phone) && <span><Phone className="inline h-3 w-3 mr-0.5" />{p.mobile || p.phone}</span>}
        </span>
        {isAdmin && (
          <Button variant="ghost" size="icon" className="ml-auto h-7 w-7 text-red-600" title="Rimuovi dall'unità" onClick={() => rimuovi(rel)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="albero-anagrafica">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Cerca unità o soggetto…" value={cerca} onChange={(e) => setCerca(e.target.value)} className="max-w-xs" data-testid="albero-cerca" />
        <Button variant={vista === 'unita' ? 'default' : 'outline'} size="sm" onClick={() => setVista('unita')}><Building2 className="h-4 w-4 mr-1" />Per unità</Button>
        <Button variant={vista === 'soggetti' ? 'default' : 'outline'} size="sm" onClick={() => setVista('soggetti')}><User className="h-4 w-4 mr-1" />Per soggetto</Button>
        {vista === 'unita' && (
          <>
            <Button variant="ghost" size="sm" onClick={tuttiAperti}>Apri tutto</Button>
            <Button variant="ghost" size="sm" onClick={tuttiChiusi}>Chiudi tutto</Button>
            <label className="flex items-center gap-1 text-sm ml-auto cursor-pointer">
              <input type="checkbox" checked={mostraStorico} onChange={(e) => setMostraStorico(e.target.checked)} /> mostra storico
            </label>
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {units.length} unità · {people.length} soggetti · {rels.filter(attiva).length} titolarità attive. I dati sono quelli inseriti nell'app: ogni modifica fatta qui è definitiva.
      </p>

      {loading && <div className="text-sm text-muted-foreground">Caricamento…</div>}

      {!loading && vista === 'unita' && (
        <Card>
          <CardContent className="p-0 divide-y">
            {unitaVisibili.map((u) => {
              const gruppi = albero[u.id] || {};
              const attive = GRUPPI.filter((g) => !['STORICO', 'FUTURO'].includes(g.key)).reduce((n, g) => n + (gruppi[g.key]?.length || 0), 0);
              const aperta = !!aperte[u.id] || !!q;
              return (
                <div key={u.id} data-testid={`albero-unita-${u.unit_number}`}>
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-stone-50 cursor-pointer" onClick={() => toggle(u.id)}>
                    {aperta ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-semibold w-12">{u.unit_number}</span>
                    <span className="text-sm text-muted-foreground">
                      {[DESTINAZIONI[u.destination] || u.destination, u.floor != null ? `piano ${u.floor}` : null, u.building].filter(Boolean).join(' · ')}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2 flex items-center gap-1">
                      <Landmark className="h-3 w-3" />{catastoRiassunto(u) || <em>dati catastali mancanti</em>}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">{attive} soggett{attive === 1 ? 'o' : 'i'}</span>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7" onClick={(e) => { e.stopPropagation(); apriCatasto(u); }} title="Modifica dati catastali" data-testid={`albero-catasto-${u.unit_number}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />Catasto
                      </Button>
                    )}
                  </div>
                  {aperta && (
                    <div className="px-3 pb-2">
                      {GRUPPI.filter((g) => g.key !== 'STORICO' || mostraStorico).map((g) => (
                        <div key={g.key} className="mt-1">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground pl-4 pt-1">
                            {g.label}
                            {isAdmin && !['STORICO', 'FUTURO'].includes(g.key) && (
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => apriNuovo(u, g)} data-testid={`albero-aggiungi-${u.unit_number}-${g.key}`}>
                                <Plus className="h-3 w-3 mr-1" />aggiungi
                              </Button>
                            )}
                          </div>
                          {(gruppi[g.key] || []).length === 0 && <div className="pl-8 text-xs text-muted-foreground/70 py-1">—</div>}
                          {(gruppi[g.key] || []).map((r) => <RigaPersona key={r.id} rel={r} />)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {unitaVisibili.length === 0 && <div className="p-4 text-sm text-muted-foreground">Nessuna unità trovata</div>}
          </CardContent>
        </Card>
      )}

      {!loading && vista === 'soggetti' && (
        <Card>
          <CardContent className="p-0 divide-y">
            {people.filter((p) => !q || (p.full_name || '').toLowerCase().includes(q)).map((p) => {
              const sue = (perSoggetto[p.id] || []).slice().sort((a, b) => ordinaUnita({ unit_number: a.unit_number }, { unit_number: b.unit_number }));
              const att = sue.filter(attiva);
              return (
                <div key={p.id} className="px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`albero-soggetto-${p.id}`}>
                  <User className="h-4 w-4 text-primary" />
                  <span className="font-medium">{p.full_name}</span>
                  {att.length === 0 && <Badge variant="outline" className="text-amber-700 border-amber-300">senza titolarità attiva</Badge>}
                  <span className="flex flex-wrap gap-1 ml-2">
                    {(mostraStorico ? sue : att).map((r) => {
                      const ruolo = RUOLI[r.relationship_type] || { label: r.relationship_type, color: 'bg-gray-100 text-gray-800' };
                      return (
                        <Badge key={r.id} className={ruolo.color} title={ruolo.label}>
                          {r.unit_number} · {ruolo.label.toLowerCase()}{r.ownership_percentage ? ` ${r.ownership_percentage}%` : ''}{!attiva(r) ? ' (chiusa)' : ''}
                        </Badge>
                      );
                    })}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto">{p.pec || p.email || ''}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* dati catastali */}
      <Dialog open={!!catastoUnit} onOpenChange={(v) => { if (!v) setCatastoUnit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dati catastali — unità {catastoUnit?.unit_number}</DialogTitle>
            <DialogDescription>Foglio, particella, subalterno, categoria e rendita come da visura.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[['cadastral_sheet', 'Foglio'], ['cadastral_parcel', 'Particella'], ['cadastral_subordinate', 'Subalterno'],
              ['cadastral_category', 'Categoria (es. A/2)'], ['cadastral_zone', 'Zona censuaria'], ['cadastral_income', 'Rendita (€)']].map(([k, lbl]) => (
              <div key={k}>
                <Label htmlFor={`cat-${k}`}>{lbl}</Label>
                <Input id={`cat-${k}`} value={catasto[k] ?? ''} onChange={(e) => setCatasto({ ...catasto, [k]: e.target.value })} data-testid={`catasto-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatastoUnit(null)}>Annulla</Button>
            <Button onClick={salvaCatasto} disabled={salvando} data-testid="catasto-salva">{salvando ? 'Salvo…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* nuovo soggetto sull'unità */}
      <Dialog open={!!nuovo} onOpenChange={(v) => { if (!v) setNuovo(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi soggetto a {nuovo?.unit?.unit_number} — {nuovo?.gruppo?.label}</DialogTitle>
            <DialogDescription>Il soggetto deve già esistere nella scheda «Soggetti»; qui lo si collega all'unità con il suo ruolo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Soggetto</Label>
              <Input placeholder="Cerca per nome…" value={cercaPersona} onChange={(e) => setCercaPersona(e.target.value)} className="mb-1" />
              <Select value={nuovoForm.person_id} onValueChange={(v) => setNuovoForm({ ...nuovoForm, person_id: v })}>
                <SelectTrigger data-testid="nuovo-soggetto-select"><SelectValue placeholder="Scegli il soggetto" /></SelectTrigger>
                <SelectContent>
                  {personeFiltrate.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ruolo</Label>
                <Select value={nuovoForm.relationship_type} onValueChange={(v) => setNuovoForm({ ...nuovoForm, relationship_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(nuovo?.gruppo?.roles || Object.keys(RUOLI)).map((r) => <SelectItem key={r} value={r}>{RUOLI[r].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quota (%)</Label>
                <Input value={nuovoForm.ownership_percentage} onChange={(e) => setNuovoForm({ ...nuovoForm, ownership_percentage: e.target.value })} placeholder="es. 50" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={nuovoForm.to_verify} onChange={(e) => setNuovoForm({ ...nuovoForm, to_verify: e.target.checked })} /> titolarità da verificare
            </label>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={nuovoForm.notes} onChange={(e) => setNuovoForm({ ...nuovoForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovo(null)}>Annulla</Button>
            <Button onClick={salvaNuovo} disabled={salvando} data-testid="nuovo-soggetto-salva">{salvando ? 'Salvo…' : 'Collega'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreeTab;
